/**
 * Test Page per ChecklistItem Implementation
 * 
 * Pagina di test per verificare l'integrazione completa:
 * - Kanban Board funzionante
 * - Task Detail Modal con tabs
 * - ChecklistManager CRUD
 * - Statistiche e progresso
 */

import React, { useState } from 'react';
import {
  AppShell,
  Container,
  Title,
  Group,
  Button,
  Paper,
  Stack,
  Text,
  Badge,
  Grid,
  Alert,
  Tabs,
} from '@mantine/core';
import { 
  IconChecklist, 
  IconLayoutKanban, 
  IconChartBar, 
  IconTestPipe,
  IconCheck,
  IconAlertCircle,
  IconInfoCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import KanbanBoard from '../components/kanban/KanbanBoard';
import TaskDetailModal from '../components/modals/TaskDetailModal';
import { checklistItemService } from '../services/checklistItemService';

const ChecklistTestPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string | null>('kanban');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number>(1);
  const [testResults, setTestResults] = useState<{
    kanban: boolean;
    modal: boolean;
    checklist: boolean;
    api: boolean;
  }>({
    kanban: false,
    modal: false,
    checklist: false,
    api: false
  });

  // Test API Connection
  const testAPIConnection = async () => {
    try {
      const result = await checklistItemService.getAll();
      setTestResults(prev => ({ ...prev, api: true }));
      
      notifications.show({
        title: 'Test API',
        message: `✅ API funzionante - ${result.items.length} items trovati`,
        color: 'green'
      });
      
      return true;
    } catch (error) {
      setTestResults(prev => ({ ...prev, api: false }));
      
      notifications.show({
        title: 'Test API',
        message: '❌ Errore nella connessione API',
        color: 'red'
      });
      
      console.error('API Test Error:', error);
      return false;
    }
  };

  // Test Kanban Board
  const testKanbanBoard = () => {
    setTestResults(prev => ({ ...prev, kanban: true }));
    
    notifications.show({
      title: 'Test Kanban',
      message: '✅ Kanban Board caricato correttamente',
      color: 'green'
    });
  };

  // Test Task Modal
  const testTaskModal = (taskId: number = 1) => {
    setSelectedTaskId(taskId);
    setShowTaskModal(true);
    setTestResults(prev => ({ ...prev, modal: true }));
    
    notifications.show({
      title: 'Test Modal',
      message: '✅ Task Detail Modal aperto',
      color: 'green'
    });
  };

  // Test Checklist Operations
  const testChecklistOperations = () => {
    setTestResults(prev => ({ ...prev, checklist: true }));
    
    notifications.show({
      title: 'Test Checklist',
      message: '✅ Operazioni checklist simulate',
      color: 'green'
    });
  };

  // Run All Tests
  const runAllTests = async () => {
    notifications.show({
      title: 'Test Suite',
      message: '🧪 Avvio test completi...',
      color: 'blue'
    });

    // Reset risultati
    setTestResults({
      kanban: false,
      modal: false,
      checklist: false,
      api: false
    });

    // Sequenza test
    setTimeout(() => testKanbanBoard(), 500);
    setTimeout(() => testChecklistOperations(), 1000);
    setTimeout(() => testTaskModal(), 1500);
    setTimeout(async () => {
      await testAPIConnection();
      
      notifications.show({
        title: 'Test Suite Completata',
        message: '✅ Tutti i test sono stati eseguiti',
        color: 'green'
      });
    }, 2000);
  };

  const getStatusIcon = (status: boolean) => {
    return status ? (
      <IconCheck size={16} color="green" />
    ) : (
      <IconAlertCircle size={16} color="orange" />
    );
  };

  const getStatusBadge = (status: boolean) => {
    return (
      <Badge
        color={status ? 'green' : 'orange'}
        variant="filled"
      >
        {status ? 'PASS' : 'PENDING'}
      </Badge>
    );
  };

  return (
    <AppShell>
      <Container size="xl" py="md">
        <Stack gap="lg">
          {/* Header */}
          <Paper p="md" withBorder>
            <Group justify="space-between">
              <div>
                <Title order={2}>
                  <IconTestPipe size={28} /> ChecklistItem Test Suite
                </Title>
                <Text c="dimmed" size="sm">
                  Pagina di test per l'implementazione completa ChecklistItem
                </Text>
              </div>
              <Button
                leftSection={<IconTestPipe size={16} />}
                onClick={runAllTests}
                variant="filled"
                color="blue"
              >
                Esegui Tutti i Test
              </Button>
            </Group>
          </Paper>

          {/* Test Status Dashboard */}
          <Grid>
            <Grid.Col span={3}>
              <Paper p="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed">Kanban Board</Text>
                    <Group gap="xs">
                      {getStatusIcon(testResults.kanban)}
                      <Text fw={500}>Rendering</Text>
                    </Group>
                  </div>
                  {getStatusBadge(testResults.kanban)}
                </Group>
              </Paper>
            </Grid.Col>

            <Grid.Col span={3}>
              <Paper p="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed">Task Modal</Text>
                    <Group gap="xs">
                      {getStatusIcon(testResults.modal)}
                      <Text fw={500}>Apertura</Text>
                    </Group>
                  </div>
                  {getStatusBadge(testResults.modal)}
                </Group>
              </Paper>
            </Grid.Col>

            <Grid.Col span={3}>
              <Paper p="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed">Checklist Mgr</Text>
                    <Group gap="xs">
                      {getStatusIcon(testResults.checklist)}
                      <Text fw={500}>CRUD Ops</Text>
                    </Group>
                  </div>
                  {getStatusBadge(testResults.checklist)}
                </Group>
              </Paper>
            </Grid.Col>

            <Grid.Col span={3}>
              <Paper p="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed">Backend API</Text>
                    <Group gap="xs">
                      {getStatusIcon(testResults.api)}
                      <Text fw={500}>Connessione</Text>
                    </Group>
                  </div>
                  {getStatusBadge(testResults.api)}
                </Group>
              </Paper>
            </Grid.Col>
          </Grid>

          {/* Instructions */}
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="Istruzioni per il Test"
            color="blue"
          >
            <Stack gap="xs">
              <Text size="sm">
                1. <strong>Kanban Board</strong>: Verifica che le colonne si carichino e il drag & drop funzioni
              </Text>
              <Text size="sm">
                2. <strong>Task Modal</strong>: Clicca su un task per aprire il modal con i tab Details/Checklist
              </Text>
              <Text size="sm">
                3. <strong>Checklist Manager</strong>: Nel tab Checklist, prova ad aggiungere/completare/eliminare items
              </Text>
              <Text size="sm">
                4. <strong>API Backend</strong>: Verifica che le chiamate API funzionino (richiede backend attivo)
              </Text>
            </Stack>
          </Alert>

          {/* Main Test Interface */}
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="kanban" leftSection={<IconLayoutKanban size={14} />}>
                Kanban Board
              </Tabs.Tab>
              <Tabs.Tab value="checklist" leftSection={<IconChecklist size={14} />}>
                Checklist Test
              </Tabs.Tab>
              <Tabs.Tab value="stats" leftSection={<IconChartBar size={14} />}>
                API Statistics
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="kanban" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={3}>Kanban Board Test</Title>
                    <Group>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testTaskModal(1)}
                      >
                        Test Modal Task #1
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testTaskModal(2)}
                      >
                        Test Modal Task #2
                      </Button>
                    </Group>
                  </Group>

                  <div style={{ height: '600px' }}>
                    <KanbanBoard />
                  </div>
                </Stack>
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="checklist" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <Title order={3}>Checklist Operations Test</Title>
                  
                  <Grid>
                    <Grid.Col span={6}>
                      <Button
                        fullWidth
                        variant="outline"
                        onClick={testAPIConnection}
                      >
                        Test API Connection
                      </Button>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Button
                        fullWidth
                        variant="outline"
                        onClick={testChecklistOperations}
                      >
                        Test Checklist CRUD
                      </Button>
                    </Grid.Col>
                  </Grid>

                  <Alert title="Checklist Test Instructions">
                    <Text size="sm">
                      Usa i bottoni sopra per testare le operazioni, o apri un Task Modal dal Kanban 
                      per testare il ChecklistManager integrato.
                    </Text>
                  </Alert>
                </Stack>
              </Paper>
            </Tabs.Panel>

            <Tabs.Panel value="stats" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <Title order={3}>API Statistics</Title>
                  
                  <Text size="sm" c="dimmed">
                    Questa sezione mostrerà le statistiche delle API quando il backend sarà connesso.
                    Per ora puoi testare la connessione API dal tab Checklist.
                  </Text>
                </Stack>
              </Paper>
            </Tabs.Panel>
          </Tabs>

          {/* Task Detail Modal */}
          <TaskDetailModal
            taskId={selectedTaskId}
            opened={showTaskModal}
            onClose={() => setShowTaskModal(false)}
            onTaskUpdated={() => {
              notifications.show({
                title: 'Task Updated',
                message: '✅ Task aggiornato con successo',
                color: 'green'
              });
            }}
          />
        </Stack>
      </Container>
    </AppShell>
  );
};

export default ChecklistTestPage;